import React, { useCallback } from 'react'

import { ClassroomManager } from '@helpers'
import { useModalsDispatch, useShortcutsContext } from '@contexts'
import { useClassroomPermissions } from '../../contexts'
import { Classroom as CC } from '@constants'

const SKETCH_CLIPBOARD_CONSTANT = 'GOPEER_SKETCH_CLIPBOARD'

const SketchShortcuts = ({ canvas }) => {
  const { registerShortcut } = useShortcutsContext()
  const { open } = useModalsDispatch()
  const clipboard = React.useRef()
  const { checkPermission } = useClassroomPermissions()
  const canErase = checkPermission(CC.FEATURES.SKETCH_ERASER)
  const canUndoRedo = checkPermission(CC.FEATURES.SKETCH_UNDO_REDO)

  const copyToClipboard = useCallback((object) => {
    clipboard.current = object
    navigator?.clipboard?.writeText(SKETCH_CLIPBOARD_CONSTANT)
  }, [])

  const erase = () => {
    const activeObjects = canvas.getActiveObjects()

    if (activeObjects.length) {
      const isInEditingState = activeObjects.some((obj) => obj.isEditing)

      activeObjects
        .filter((object) => !object.isEditing)
        .forEach((obj) => canvas.remove(obj))

      if (!isInEditingState) canvas.discardActiveObject()
      return
    }
  }

  const redo = () => {
    const binding = ClassroomManager.getBinding('sketch')

    if (canvas.getActiveObject()?.isEditing) return
    if (binding) binding.redo()
  }

  const undo = () => {
    const binding = ClassroomManager.getBinding('sketch')

    if (canvas.getActiveObject()?.isEditing) return
    if (binding) binding.undo()
  }

  const copy = () => {
    const active = canvas.getActiveObject()

    if (active?.isEditing) {
      const selectedText = active.text.slice(
        active.selectionStart,
        active.selectionEnd
      )

      active.clone((cloned) => {
        cloned.text = selectedText
        copyToClipboard(cloned)
      })
      return
    }

    if (active)
      active.clone((cloned) => {
        copyToClipboard(cloned)
      })
  }

  const paste = async (event) => {
    const osClipboardData =
      event.clipboardData ||
      event.originalEvent?.clipboardData ||
      window.clipboardData

    if (osClipboardData.getData('text') !== SKETCH_CLIPBOARD_CONSTANT) return
    if (!clipboard.current) return
    if (canvas.getActiveObject()?.isEditing) return

    clipboard.current.clone((cloned) => {
      canvas.discardActiveObject()

      cloned.set({
        id: `${cloned.type}-${Date.now()}`,
        left: cloned.left + 10,
        top: cloned.top + 10
      })

      if (cloned.type === 'activeSelection') {
        cloned.canvas = canvas
        cloned.forEachObject((obj) => canvas.add(obj))
        cloned.setCoords()
      } else {
        canvas.add(cloned)
      }

      clipboard.current = cloned
      clipboard.current.top += 10
      clipboard.current.left += 10
      canvas.setActiveObject(cloned)
      canvas.requestRenderAll()
    })
  }

  const cut = () => {
    copy()
    erase()
  }

  const openFormulaGraphModal = () => {
    open({ name: 'formula-graph-modal' })
  }

  React.useEffect(() => {
    if (!canvas) return

    if (canErase) {
      registerShortcut('Backspace', erase)
    }

    if (canUndoRedo) {
      registerShortcut('CtrlOrCmd+z', undo)
      registerShortcut('CtrlOrCmd+Shift+z', redo)
    }

    registerShortcut('CtrlOrCmd+c', copy)
    document.addEventListener('paste', paste)
    registerShortcut('CtrlOrCmd+x', cut)
    registerShortcut('CtrlOrCmd+Shift+o', openFormulaGraphModal)

    return () => {
      document.removeEventListener('paste', paste)
    }
  }, [canvas]) // eslint-disable-line

  return null
}

export default SketchShortcuts
